import { Box, Button, Chip, Stack, Tooltip } from '@mui/material';
import { compact, flatMap, isEmpty, values } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';

import { casingFormat, formatStateFilterLabel } from 'common/utils';
import { updateFillLaterFilter } from 'store/thunks/workQueueThunks';

import { FILL_LATER_STATUS_LABEL, FILL_LATER_TYPE_LABEL } from '../constants/fillLaterEligibility';
import FacilityFilterStatusChip from './FacilityFilterStatusChip';

const formatDateForChip = (isoDate) => {
  if (!isoDate) return '…';
  const [year, month, day] = isoDate.split('-');
  if (!year || !month || !day) return isoDate;
  return `${month}-${day}-${year}`;
};

const FillLaterQueueFilterStatusBar = () => {
  const dispatch = useDispatch();
  const filter = useSelector(({ workQueue }) => workQueue.fillLater?.filter);
  const partners = useSelector(({ auth, partner }) => partner[auth.currentTenantId]);
  // Practices hang off each partner (partnerSlice.loadPartnerPractices) once a partner filter
  // has loaded them; flatten so a practice chip can show its name.
  const practices = flatMap(partners, (p) => p.partnerPractices ?? []);

  const removeFilter = (filterName) => () =>
    dispatch(updateFillLaterFilter({ filter: { ...filter, [filterName]: undefined } }));

  // "Remove all" clears every chip, the default Status chip included, so the queue then lists
  // every status (the backend applies no status clause when none is sent). Same as Central Fill.
  const removeAllFilter = () => dispatch(updateFillLaterFilter({ filter: {} }));

  const nameOf = (list, idKey, nameKey, ids) =>
    (list ?? [])
      .filter((entry) => ids.includes(entry[idKey]))
      .map((entry) => casingFormat(entry[nameKey]))
      .join(', ');

  // Every chip on the bar is removable, the default Status chip included, so Remove All shows
  // whenever any chip does (QA: the default chip had no Remove All and survived Remove All).
  const hasActiveFilters = !isEmpty(compact(values(filter)));

  return (
    <Stack spacing={1} direction='row' flexWrap='wrap' rowGap={1} alignItems='center'>
      {!isEmpty(filter?.statuses) && (
        <Chip
          label={`Status = ${filter.statuses
            .map((s) => FILL_LATER_STATUS_LABEL[s] ?? s)
            .join(', ')}`}
          onDelete={removeFilter('statuses')}
        />
      )}

      {!isEmpty(filter?.fillLaterTypes) && (
        <Chip
          label={`Type = ${filter.fillLaterTypes
            .map((t) => FILL_LATER_TYPE_LABEL[t] ?? t)
            .join(', ')}`}
          onDelete={removeFilter('fillLaterTypes')}
        />
      )}

      {(filter?.scheduledAt || filter?.scheduledBetween) && (
        <Chip
          label={
            filter.scheduledAt
              ? `Scheduled = ${formatDateForChip(filter.scheduledAt)}`
              : `Scheduled = ${formatDateForChip(
                  filter.scheduledBetween[0]
                )} – ${formatDateForChip(filter.scheduledBetween[1])}`
          }
          onDelete={() =>
            dispatch(
              updateFillLaterFilter({
                filter: { ...filter, scheduledAt: undefined, scheduledBetween: undefined },
              })
            )
          }
        />
      )}

      {filter?.patientName && (
        <Chip label={`Patient = ${filter.patientName}`} onDelete={removeFilter('patientName')} />
      )}

      {filter?.orderId && (
        <Chip label={`Order Id = ${filter.orderId}`} onDelete={removeFilter('orderId')} />
      )}

      {filter?.rxNumber && (
        <Chip label={`Rx Number = ${filter.rxNumber}`} onDelete={removeFilter('rxNumber')} />
      )}

      {filter?.drugName && (
        <Chip label={`Drug Name = ${filter.drugName}`} onDelete={removeFilter('drugName')} />
      )}

      {!isEmpty(filter?.drugNames) && (
        <Chip
          label={
            <Tooltip title={filter.drugNames.join(', ')}>
              <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {formatStateFilterLabel(filter.drugNames, 'Drug Name')}
              </Box>
            </Tooltip>
          }
          onDelete={removeFilter('drugNames')}
        />
      )}

      {filter?.quantity && (
        <Chip label={`Qty = ${filter.quantity}`} onDelete={removeFilter('quantity')} />
      )}

      {!isEmpty(filter?.ndcs) && (
        <Chip
          label={
            <Tooltip title={filter.ndcs.join(', ')}>
              <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {formatStateFilterLabel(filter.ndcs, 'NDC')}
              </Box>
            </Tooltip>
          }
          onDelete={removeFilter('ndcs')}
        />
      )}

      {!isEmpty(filter?.partnerIds) && (
        <Chip
          label={`Partner = ${
            nameOf(partners, 'partnerUuid', 'partnerName', filter.partnerIds) ||
            `${filter.partnerIds.length} selected`
          }`}
          onDelete={removeFilter('partnerIds')}
        />
      )}

      {!isEmpty(filter?.practiceIds) && (
        <Chip
          label={`Practice = ${
            nameOf(practices, 'practiceId', 'practiceName', filter.practiceIds) ||
            `${filter.practiceIds.length} selected`
          }`}
          onDelete={removeFilter('practiceIds')}
        />
      )}

      <FacilityFilterStatusChip
        selectedFacilities={filter?.fulfillingPharmacyIds}
        removeFilter={removeFilter}
      />

      {hasActiveFilters && <Button onClick={removeAllFilter}>Remove All</Button>}
    </Stack>
  );
};

export default FillLaterQueueFilterStatusBar;
